import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import colors from 'theme/colors';
import sizes from 'theme/sizes';
import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Text from 'components/_UI/Text';
import { FullyCentered, HorizontalCenter } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { StyledForm } from 'styled/UI/StyledForm';
import signInTexts from 'texts/general/sign-in';
import Divider from 'components/_UI/Divider';
import Icon from 'components/_UI/Icon';
import breakpoints from 'theme/breakpoints';
import { useStepRegister } from 'context/login/StepRegister';
import loginApi from 'api/requests/login';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Loader from 'components/_UI/Loader';
import fieldValidations from 'helpers/fieldValidations';
import paths from 'routes/paths';
import { useUser } from 'context/user/User';
import GoogleSignIn from './GoogleSignIn';
import buttonText from 'texts/buttons/buttonText';
import Modal from 'components/_UI/Modal';

function SignIn() {
    const { setStepRegister } = useStepRegister();
    const { user, setUser } = useUser();

    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm();

    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [modalVerifyOpen, setModalVerifyOpen] = useState(false);
    const [showResendEmailMessage, setShowResendEmailMessage] = useState(false);

    const login = async ({ email, password }) => {
        const payload = { email, password };

        setUser(payload);
        setIsLoading(true);

        try {
            const { data } = await loginApi.signIn(payload);
          
            localStorage.setItem('token', data.accessToken);
            localStorage.setItem('user', JSON.stringify(data));
            
            setUser(data);
            
            window.location.href = paths.home;
            
        } catch (error) {
            setErrorMessage(error?.response?.data?.message);
            setShowResendEmailMessage(false);

            if (error.response.status === 403) {
                setShowResendEmailMessage(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const resendConfirmCode = async () => {
        try {
            await loginApi.resendConfirmCode(user);
            setModalVerifyOpen(true);
        } catch (error) {
            setErrorMessage(error?.response?.data?.message);
        }
    }


    return (
        <BoxContainer fullyCentralized>
            <LogoContainer>
                <Icon name={'whizr-logo'} color={colors.primary} />
            </LogoContainer>

            <StyledForm onSubmit={handleSubmit(login)}>
                <Fieldset
                    placeholder={signInTexts.inputs.username}
                    badgeIcon={'avatar'}
                    badgePosition={'left'}
                    name="email"
                    register={register}
                    validations={fieldValidations.email()}
                />
                {errors.email?.message && (
                    <HorizontalCenter>
                        <Text color={colors.danger}> {errors.email?.message} </Text>
                    </HorizontalCenter>
                )}

                <Fieldset
                    placeholder={signInTexts.inputs.password}
                    badgeIcon={'lock'}
                    badgePosition={'left'}
                    marginTop={sizes.big}
                    inputType='password'
                    name="password"
                    register={register}
                    validations={fieldValidations.passwordWithoutPatterns()}
                />
                {
                    errors.password?.message && (
                        <HorizontalCenter>
                            <Text color={colors.danger}> {errors.password?.message} </Text>
                        </HorizontalCenter>
                    )
                }
                {!isLoading ? (
                    <Button
                        variant='primary'
                        size='large'
                        marginTop={sizes.normal}
                        type='submit'
                    >
                        {signInTexts.buttons.enter}
                    </Button>
                ) : <Loader />}

            </StyledForm>


            {errorMessage && (
                <>
                    <Text
                        color={colors.danger}
                        type='bold'
                        marginTop={sizes.small}
                    >
                        {errorMessage}
                    </Text>
                    {showResendEmailMessage && (
                        <React.Fragment>
                            <Text name='small' marginBottom={sizes.quark}>
                                {signInTexts.texts.resendEmailText}
                            </Text>
                            <Button
                                onClick={() => resendConfirmCode()}
                                category='outline'
                                size='small'
                                marginBottom='30px'>
                                {signInTexts.texts.resendEmailButton}
                            </Button>
                        </React.Fragment>
                    )}
                </>
            )}

            <Text name='small'>{signInTexts.texts.forgotPassword}</Text>
            <Button
                path={paths.recoverAccount}
                size='small'
                category='outline'
            >
                {signInTexts.texts.restoreAccount}
            </Button>

            <Modal isOpen={modalVerifyOpen} onClose={() => setModalVerifyOpen(false)}>
                <Text
                    name='default'
                    marginTop='20px'
                    marginBottom='20px'
                    type='bold'>
                    {signInTexts.texts.modalVerify}
                </Text>
                <Button onClick={() => setModalVerifyOpen(false)}>
                    {buttonText.understood}
                </Button>
            </Modal>

            <Divider />

            <Column fullyCentralized marginTop={sizes.medium} >
                <Text name='small'>{signInTexts.texts.dontHaveAccount}</Text>
                <Button
                    path={paths.register}
                    variant='secondary'
                    size='large'
                    marginTop={sizes.nano}
                    onClick={() => setStepRegister(1)}
                >
                    {signInTexts.buttons.newAccount}
                </Button>
            </Column>

            <GoogleSignIn />
        </BoxContainer>
    )
};

const BoxContainer = styled(FullyCentered)`
    flex-direction: column;
    max-width: 420px;
    height: 100%;
    padding: 40px;
    background: ${colors.neutralDark};
`;

const LogoContainer = styled.div`
    width: 110px;
    height: 110px;
    margin-bottom: ${sizes.gigantic};

    // @media screen and (min-width: ${breakpoints.lg}) {
    //     display: none;
    // }
`;

export default SignIn;